import type { jsPDF } from 'jspdf';
import type { PdfExportFontFamily } from './export-utils';

export type { PdfExportFontFamily };

type PdfFontStyle = 'normal' | 'bold' | 'italic' | 'bolditalic';

type CachedPdfFont = {
  fileName: string;
  family: PdfExportFontFamily;
  style: PdfFontStyle;
  base64: string;
};

type AssetFontSpec = {
  url: string;
  diskRelativePath: string;
};

const FAMILY_BY_PACKAGE_NAME: Record<string, PdfExportFontFamily> = {
  'Liberation Serif': 'LiberationSerif',
  'Liberation Sans': 'LiberationSans',
};

const ASSET_FONTS: Record<
  Exclude<PdfExportFontFamily, 'LiberationSerif' | 'LiberationSans'>,
  Record<PdfFontStyle, AssetFontSpec>
> = {
  Gelasio: {
    normal: {
      url: new URL('../assets/fonts/pdf-export/Gelasio-Regular.ttf', import.meta.url).href,
      diskRelativePath: 'src/assets/fonts/pdf-export/Gelasio-Regular.ttf',
    },
    bold: {
      url: new URL('../assets/fonts/pdf-export/Gelasio-Bold.ttf', import.meta.url).href,
      diskRelativePath: 'src/assets/fonts/pdf-export/Gelasio-Bold.ttf',
    },
    italic: {
      url: new URL('../assets/fonts/pdf-export/Gelasio-Italic.ttf', import.meta.url).href,
      diskRelativePath: 'src/assets/fonts/pdf-export/Gelasio-Italic.ttf',
    },
    bolditalic: {
      url: new URL('../assets/fonts/pdf-export/Gelasio-BoldItalic.ttf', import.meta.url).href,
      diskRelativePath: 'src/assets/fonts/pdf-export/Gelasio-BoldItalic.ttf',
    },
  },
  EBGaramond: {
    normal: {
      url: new URL('../assets/fonts/pdf-export/EBGaramond-Regular.ttf', import.meta.url).href,
      diskRelativePath: 'src/assets/fonts/pdf-export/EBGaramond-Regular.ttf',
    },
    bold: {
      url: new URL('../assets/fonts/pdf-export/EBGaramond-Bold.ttf', import.meta.url).href,
      diskRelativePath: 'src/assets/fonts/pdf-export/EBGaramond-Bold.ttf',
    },
    italic: {
      url: new URL('../assets/fonts/pdf-export/EBGaramond-Italic.ttf', import.meta.url).href,
      diskRelativePath: 'src/assets/fonts/pdf-export/EBGaramond-Italic.ttf',
    },
    bolditalic: {
      url: new URL('../assets/fonts/pdf-export/EBGaramond-BoldItalic.ttf', import.meta.url).href,
      diskRelativePath: 'src/assets/fonts/pdf-export/EBGaramond-BoldItalic.ttf',
    },
  },
  DejaVuSans: {
    normal: {
      url: new URL('../assets/fonts/pdf-export/DejaVuSans-Regular.ttf', import.meta.url).href,
      diskRelativePath: 'src/assets/fonts/pdf-export/DejaVuSans-Regular.ttf',
    },
    bold: {
      url: new URL('../assets/fonts/pdf-export/DejaVuSans-Bold.ttf', import.meta.url).href,
      diskRelativePath: 'src/assets/fonts/pdf-export/DejaVuSans-Bold.ttf',
    },
    italic: {
      url: new URL('../assets/fonts/pdf-export/DejaVuSans-Italic.ttf', import.meta.url).href,
      diskRelativePath: 'src/assets/fonts/pdf-export/DejaVuSans-Italic.ttf',
    },
    bolditalic: {
      url: new URL('../assets/fonts/pdf-export/DejaVuSans-BoldItalic.ttf', import.meta.url).href,
      diskRelativePath: 'src/assets/fonts/pdf-export/DejaVuSans-BoldItalic.ttf',
    },
  },
};

const toPdfFontStyle = (fontWeight: number, fontStyle: 'normal' | 'italic'): PdfFontStyle => {
  const isBold = fontWeight >= 700;
  if (isBold && fontStyle === 'italic') {
    return 'bolditalic';
  }
  if (isBold) {
    return 'bold';
  }
  if (fontStyle === 'italic') {
    return 'italic';
  }
  return 'normal';
};

const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk as unknown as number[]);
  }
  return btoa(binary);
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string =>
  uint8ArrayToBase64(new Uint8Array(buffer));

const isNodeRuntime = (): boolean =>
  typeof process !== 'undefined' && Boolean(process.versions?.node);

const loadAssetFontAsBase64 = async (spec: AssetFontSpec): Promise<string> => {
  if (isNodeRuntime()) {
    const { existsSync, readFileSync } = await import('node:fs');
    const { resolve } = await import('node:path');
    const { fileURLToPath } = await import('node:url');
    const candidates = [
      resolve(process.cwd(), spec.diskRelativePath),
      resolve(process.cwd(), 'apps/web', spec.diskRelativePath),
    ];
    if (spec.url.startsWith('file:')) {
      candidates.unshift(fileURLToPath(spec.url));
    }
    for (const candidate of candidates) {
      if (existsSync(candidate)) {
        return uint8ArrayToBase64(new Uint8Array(readFileSync(candidate)));
      }
    }
    throw new Error(
      `PDF export font not found on disk: ${spec.diskRelativePath} (cwd=${process.cwd()})`,
    );
  }

  const response = await fetch(spec.url);
  if (!response.ok) {
    throw new Error(`Failed to load PDF font from ${spec.url}`);
  }
  return arrayBufferToBase64(await response.arrayBuffer());
};

let cachedLiberationFontsPromise: Promise<CachedPdfFont[]> | null = null;
const cachedAssetFonts = new Map<PdfExportFontFamily, Promise<CachedPdfFont[]>>();

const loadLiberationFonts = (): Promise<CachedPdfFont[]> => {
  if (!cachedLiberationFontsPromise) {
    cachedLiberationFontsPromise = import('@formepdf/fonts-standard').then(({ standardFonts }) => {
      return standardFonts()
        .filter((font) => Boolean(FAMILY_BY_PACKAGE_NAME[font.family]))
        .map((font) => {
          const family = FAMILY_BY_PACKAGE_NAME[font.family];
          const style = toPdfFontStyle(font.fontWeight, font.fontStyle);
          return {
            fileName: `${family}-${style}.ttf`,
            family,
            style,
            base64: uint8ArrayToBase64(font.src),
          };
        });
    });
  }
  return cachedLiberationFontsPromise;
};

const loadAssetFontFamily = (family: keyof typeof ASSET_FONTS): Promise<CachedPdfFont[]> => {
  const existing = cachedAssetFonts.get(family);
  if (existing) {
    return existing;
  }
  const specs = ASSET_FONTS[family];
  const promise = Promise.all(
    (Object.entries(specs) as Array<[PdfFontStyle, AssetFontSpec]>).map(async ([style, spec]) => {
      const base64 = await loadAssetFontAsBase64(spec);
      return {
        fileName: `${family}-${style}.ttf`,
        family,
        style,
        base64,
      };
    }),
  );
  cachedAssetFonts.set(family, promise);
  return promise;
};

const loadFontsForFamily = async (family: PdfExportFontFamily): Promise<CachedPdfFont[]> => {
  if (family === 'LiberationSerif' || family === 'LiberationSans') {
    const fonts = await loadLiberationFonts();
    return fonts.filter((font) => font.family === family);
  }
  return loadAssetFontFamily(family);
};

export const ensurePdfExportFonts = async (
  pdf: jsPDF,
  family: PdfExportFontFamily = 'LiberationSerif',
): Promise<void> => {
  const fonts = await loadFontsForFamily(family);
  fonts.forEach((font) => {
    pdf.addFileToVFS(font.fileName, font.base64);
    pdf.addFont(font.fileName, font.family, font.style);
  });
};
